import React, { useEffect, useMemo, useState } from 'react'
import { Form, message, Checkbox, TimePicker as AntTimePicker, Button } from "antd";
import dayjs from "dayjs";
import CommonDialog from '../../../global/dialog';
import CommonForm from '../../../global/form';
import CommonTabs from '../../../global/tabs';
import SOURCES from '../../../../utils/constants';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import { useLeadSources } from "../../../../queries/common/use-lead-source";
import { capitalizeFirstLetter } from '../../../../utils/functions';
import { useQueryClient } from '@tanstack/react-query';
import CommonSelect from '../../../global/select';
import AlertUserCard from './alertUserCard'
import CommonCheckbox from '../../../global/checkbox';
import Spinner from '../../../global/spinner';
import CommonRadio from '../../../global/radio';
import CommonCard from '../../../global/card';

const EnhanceAddLeadSource = ({ showForm, setShowForm, editLeadSource, keyParams, getLeadSources }) => {
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    // State management
    const [activeTab, setActiveTab] = useState("1");
    const [leadSourceSelected, setLeadSourceSelected] = useState("");
    const [emailPassword, setEmailPassword] = useState({
        email: "",
        password: ""
    })
    const [destinationIds, setDestinationIds] = useState([]);
    const [alertUserId, setAlertUserId] = useState([]);
    const [alertSendType, setAlertSendType] = useState(null);
    const [alertSendStartTime, setAlertSendStartTime] = useState(null);
    const [alertSendEndTime, setAlertSendEndTime] = useState(null);
    const [timezoneSelected, setTimezoneSelected] = useState("America/Los_Angeles");
    const [leadProvider, setLeadProvider] = useState(null);
    const [providersList, setProvidersList] = useState([]);
    const [extraInfo, setExtraInfo] = useState(null);
    const [showTripleseatLocation, setShowTripleseatLocation] = useState(false);
    const [showMailChimpCampaigns, setShowMailChimpCampaigns] = useState(false);
    const [locationIdTripleseat, setLocationIdTripleseat] = useState(null);
    const [campaignIdMailChimp, setCampaignIdMailChimp] = useState("");
    const [activateJob, setActivateJob] = useState(false);

    // const encodedPassword = useMemo(() => encodeURIComponent(emailPassword.password || ""), [emailPassword.password]);

    // Fetch lead source options
    const { data: leadSourceOptions, isLoading: leadSourceOptionsLoading } = useLeadSources({
        endpoint: "leadsource/options"
    });

    // Fetch destinations
    const { data: destinations, isLoading: destinationsLoading } = useGenericQuery({
        queryKey: QUERY_KEYS.destinations.all,
        endpoint: "integration",
    });

    // Fetch alert users
    const { data: alertUsers, isLoading: alertUsersLoading } = useGenericQuery({
        queryKey: QUERY_KEYS.alerts.all,
        endpoint: "alert",
    });


    // Fetch timezones
    const { data: timezones, isLoading: timezonesLoading } = useGenericQuery({
        queryKey: QUERY_KEYS.timezones.all,
        endpoint: "common/timezones",
    });

    // Fetch Tripleseat locations
    const { data: tripleseatLocations, isLoading: tripleseatLocationsLoading } = useGenericQuery({
        queryKey: QUERY_KEYS.tripleseatLocations.all,
        endpoint: "integration/tripleseat-selected-location",
        enabled: showTripleseatLocation
    });

    // Fetch MailChimp campaigns
    const { data: mailChimpCampaigns, isLoading: mailChimpCampaignsLoading } = useGenericQuery({
        queryKey: QUERY_KEYS.mailChimpCampaigns.all,
        endpoint: "integration/mailchimp-selected-campaign",
        enabled: showMailChimpCampaigns
    });

    // --- Provider Fetching Queries ---

    // Eventective Providers
    const { refetch: getProviders, isFetching: getEventectiveProvidersLoading } = useGenericQuery({
        queryKey: ["eventective-providers", emailPassword.email, emailPassword.password],
        endpoint: `leadsource/eventective/providers?email=${emailPassword.email}&password=${emailPassword.password}`,
        enabled: false,
        onSuccess: (data) => setProvidersList(data)
    });

    // WeddingSpot Venues
    const { refetch: getWeddingSpotVenues, isFetching: getWeddingSpotVenuesLoading } = useGenericQuery({
        queryKey: ["weddingspot-venues", emailPassword.email, emailPassword.password],
        endpoint: `leadsource/weddingspot/venues?email=${emailPassword.email}&password=${emailPassword.password}`,
        enabled: false,
        onSuccess: (data) => setProvidersList(data)
    });

console.log("leadSourceSelected", editLeadSource?.id);    // Knot Storefronts
    const { refetch: getKnotStorefronts, isFetching: getKnotStorefrontsLoading } = useGenericQuery({
        queryKey: ["knot-storefronts", emailPassword.email, emailPassword.password, leadSourceSelected],
        endpoint: `leadsource/knot/storefronts?email=${emailPassword.email}&password=${emailPassword.password}&leadSourceId=${editLeadSource?.id}`,
        enabled: false,
        onSuccess: (data) => setProvidersList(data)
    });

    // Yelp Locations
    const { refetch: getProvidersYelp, isFetching: getProvidersYelpLoading } = useGenericQuery({
        queryKey: ["yelp-locations", emailPassword.email, emailPassword.password],
        endpoint: `leadsource/yelp/locations?email=${emailPassword.email}&password=${emailPassword.password}`,
        enabled: false,
        onSuccess: (data) => setProvidersList(data)
    });

    // Web Links (Contact Form)
    const { data: webLinks, isLoading: webLinksLoading } = useGenericQuery({
        queryKey: ["weblinks"],
        endpoint: "weblink",
        enabled: leadSourceSelected === SOURCES.LEAD_CONTACT_FORM,
        onSuccess: (data) => {
            if (data?.results) setProvidersList(data.results);
        }
    });

    // Fetch lead source detail for editing
    const { data: leadSourceDetail, isLoading: leadSourceDetailLoading } = useGenericQuery({
        queryKey: QUERY_KEYS.leadSource.item({ id: editLeadSource?.id }),
        queryParams: { id: editLeadSource?.id },
        endpoint: "leadsource",
        enabled: editLeadSource?.isEditable && !!editLeadSource?.id
    });


    // Save mutation
    const saveLeadSourceMutation = useGenericMutation({
        endpoint: "leadsource",
        onSuccess: () => {
            message.success(editLeadSource?.isEditable ? "Lead source updated successfully." : "Lead source added successfully.");
            setShowForm(false);
            form.resetFields();
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.leadSources.lists(keyParams),
                type: "active",
                exact: true
            });
            if (getLeadSources) getLeadSources();
        },
    });

    // Load lead source detail for editing
    useEffect(() => {
        if (editLeadSource?.isEditable && editLeadSource?.id && leadSourceDetail) {
            setLeadSourceSelected(leadSourceDetail.leadSource);
            setDestinationIds(leadSourceDetail.destinations || []);
            setAlertUserId(leadSourceDetail.alertId || []);
            setAlertSendType(leadSourceDetail.alertSendType || "");
            setAlertSendStartTime(leadSourceDetail.alertTime?.startTime || null);
            setAlertSendEndTime(leadSourceDetail.alertTime?.endTime || null);
            setTimezoneSelected(leadSourceDetail.alertTime?.timezone || "America/Los_Angeles");
            setExtraInfo(leadSourceDetail.extraInfo || null);
            setLeadProvider(leadSourceDetail.extraInfo?.provider || null);
            setProvidersList(leadSourceDetail.extraInfo?.providersList || []);
            setLocationIdTripleseat(leadSourceDetail.extraInfo?.locationIdTripleseat || "");
            setCampaignIdMailChimp(leadSourceDetail.extraInfo?.campaignIdMailChimp || "");
            setActivateJob(leadSourceDetail?.active)
            setEmailPassword({
                email: leadSourceDetail.username || "",
                password: leadSourceDetail.password || ""
            });

            // Show tripleseat/mailchimp if they were selected
            if (leadSourceDetail.destinations?.includes("tripleseat")) {
                setShowTripleseatLocation(true);
            }
            if (leadSourceDetail.destinations?.includes("mailchimp")) {
                setShowMailChimpCampaigns(true);
            }

            form.setFieldsValue({
                "lead-source": leadSourceDetail.leadSource,
                "job-name": leadSourceDetail.jobName,
                "username": leadSourceDetail.username,
                "password": leadSourceDetail.password,
                "active": leadSourceDetail.active,
            });
        }
    }, [leadSourceDetail, editLeadSource?.id]);

    // Process timezones
    const timezoneOptions = useMemo(() => {
        if (!timezones || !Array.isArray(timezones)) return [];
        return timezones.map((tz) => ({
            label: `${tz.text} ${tz.utc[0]}`,
            value: tz.utc[0],
        }));
    }, [timezones]);

    // Handle form submission
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            const postData = {
                id: editLeadSource?.id || null,
                leadSource: values["lead-source"],
                jobName: values["job-name"],
                username: values["username"] || "-",
                password: values["password"] || "-",
                active: activateJob || false,
                destinations: destinationIds,
                autoresponderId: null,
                alertSendType,
                alertSendStartTime,
                alertSendEndTime,
                alertSendTimezone: timezoneSelected,
                alertId: alertUserId,
                extraInfo: {
                    ...extraInfo,
                    provider: leadProvider,
                    providersList,
                    locationIdTripleseat,
                    campaignIdMailChimp,
                },
            };

            await saveLeadSourceMutation.mutateAsync(postData);
        } catch (err) {
            console.error("Validation failed:", err);
        }
    };

    const chooseScrappingLink = (e) => {
        const val = e.target.value;
        setExtraInfo((prev) => ({
            ...prev,
            eventectiveScrapeLeads: val === "leads",
            eventectiveScrapeReferral: val === "referrals",
        }));
    };

    // Handle destination checkbox changes
    const handleDestinationChange = (checked, destinationKey) => {
        if (checked) {
            setDestinationIds([...destinationIds, destinationKey]);
            // Show additional options for tripleseat and mailchimp
            if (destinationKey === "tripleseat") {
                setShowTripleseatLocation(true);
            }
            if (destinationKey === "mailchimp") {
                setShowMailChimpCampaigns(true);
            }
        } else {
            setDestinationIds(destinationIds.filter(d => d !== destinationKey));
            // Hide additional options
            if (destinationKey === "tripleseat") {
                setShowTripleseatLocation(false);
                setLocationIdTripleseat("");
            }
            if (destinationKey === "mailchimp") {
                setShowMailChimpCampaigns(false);
                setCampaignIdMailChimp("");
            }
        }
    };

    // Handle alert user checkbox changes
    const handleAlertUserChange = (checked, userId) => {
        if (checked) {
            setAlertUserId([...alertUserId, userId]);
        } else {
            setAlertUserId(alertUserId.filter(id => id !== userId));
        }
    };

    // Credentials Tab Form Data
    const credentialsFormData = [
        {
            label: "Lead Source",
            name: "lead-source",
            inputType: SOURCES.FORMINPUTTYPES.SELECT,
            type: "select",
            placeholder: "Select Lead Source",
            required: true,
            message: "Lead source is required.",
            span: 12,
            options: Array.isArray(leadSourceOptions) ? leadSourceOptions.map(opt => ({
                label: opt.title,
                value: opt.key
            })) : [],
            onChange: (value) => {
                console.log("value", value);
                setLeadSourceSelected(value);
                setProvidersList([]);
                setLeadProvider(null);
                if (value === SOURCES.LEAD_CONTACT_FORM) {
                    form.setFieldsValue({ "username": "-", "password": "-" });
                }
            }
        },
        {
            label: "Job Name",
            name: "job-name",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "text",
            placeholder: "Enter job name",
            required: true,
            message: "Job name is required.",
            span: 12,

        },
        {
            label: "Username/Email",
            name: "username",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "text",
            placeholder: "Enter username or email",
            required: leadSourceSelected !== SOURCES.LEAD_CONTACT_FORM,
            message: "Username is required.",
            span: 12,
            hidden: leadSourceSelected === SOURCES.LEAD_CONTACT_FORM,
            onChange: (e) => {
                console.log("e", e);
                setEmailPassword((prev) => ({ ...prev, email: e.target.value }));
            }
        },
        {
            label: "Password",
            name: "password",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "password",
            validate: false,
            placeholder: "Enter password",
            required: leadSourceSelected !== SOURCES.LEAD_CONTACT_FORM,
            message: "Password is required.",
            span: 12,
            hidden: leadSourceSelected === SOURCES.LEAD_CONTACT_FORM,
            onChange: (e) => {
                console.log("e", e);
                setEmailPassword((prev) => ({ ...prev, password: e.target.value }));
            }
        },
    ];

    const refactorAlertUsers = useMemo(
        () => {
            if (!Array.isArray(alertUsers)) return [];

            return alertUsers.map((user) => ({
                id: user._id,                 // normalized id
                alertName: user.alertName ?? "",
                alertEmail: user.alertEmail ?? "",
                alertSms: user.alertSms ?? "",
                alertVia: user.alertVia ?? "",
            }));
        }, [alertUsers])

    // Tabs configuration
    const tabItems = [
        {
            key: "1",
            label: "Credentials",
            children: (
                <div className="space-y-4">
                    <CommonForm
                        data={credentialsFormData.filter(field => !field.hidden)}
                        className="mt-2"
                        layoutType="vertical"
                        form={form}
                    />

                    {/* Lead Contact Form Options */}
                    {leadSourceSelected === SOURCES.LEAD_CONTACT_FORM && (
                        <CommonCard>
                            <div className="space-y-3">
                                <h6 className="font-medium text-sm">Choose Lead Contact Form</h6>
                                <CommonSelect
                                    options={Array.isArray(providersList) ? providersList.map(lcf => ({
                                        label: lcf.formName,
                                        value: lcf._id
                                    })) : []}
                                    value={leadProvider}
                                    onChange={(value) => setLeadProvider(value)}
                                    placeholder="Select One"
                                    isLoading={webLinksLoading}
                                />
                            </div>
                        </CommonCard>
                    )}

                    {/* Eventective Options */}
                    {leadSourceSelected === SOURCES.LEAD_EVENTECTIVE && (
                        <CommonCard>
                            <h6 className="font-medium text-sm">Choose Type</h6>
                            <div className="flex gap-4 mt-1 mb-3">
                                <CommonRadio
                                    name="extrainfo"
                                    options={[
                                        {
                                            label: "Leads",
                                            value: "leads"
                                        },
                                        {
                                            label: "Referrals",
                                            value: "referrals"
                                        }
                                    ]}
                                    onChange={chooseScrappingLink}
                                    value={extraInfo?.eventectiveScrapeLeads ? "leads" : (extraInfo?.eventectiveScrapeReferral ? "referrals" : null)}
                                    className="accent-primary"
                                />
                            </div>
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <CommonSelect
                                        options={Array.isArray(providersList) ? providersList.map(opt => ({
                                            label: opt.ProviderNameFull,
                                            value: opt.ProviderNum?.toString()
                                        })) : []}
                                        value={leadProvider ?? null}
                                        onChange={(value) => setLeadProvider(value)}
                                        placeholder="Select Lead Provider"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    disabled={getEventectiveProvidersLoading}
                                    onClick={() => {
                                        if (!emailPassword.email || !emailPassword.password) {
                                            message.error("Please enter email and password");
                                            return;
                                        }
                                        getProviders();
                                    }}
                                    className="!h-10 !bg-primary text-white rounded-md text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                                >
                                    {getEventectiveProvidersLoading && <Spinner className={"!border-white"} />}
                                    Get Providers

                                </Button>
                            </div>
                        </CommonCard>
                    )}

                    {/* WeddingSpot Options */}
                    {leadSourceSelected === SOURCES.LEAD_WEDDINGSPOT && (
                        <CommonCard>
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <CommonSelect
                                        options={Array.isArray(providersList) ? providersList.map(opt => ({
                                            label: opt.name,
                                            value: opt.id?.toString()
                                        })) : []}
                                        value={leadProvider}
                                        onChange={(value) => setLeadProvider(value)}
                                        placeholder="Select Venue"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    disabled={getWeddingSpotVenuesLoading}
                                    onClick={() => {
                                        if (!emailPassword.email || !emailPassword.password) {
                                            message.error("Please enter email and password");
                                            return;
                                        }
                                        getWeddingSpotVenues();
                                    }}
                                    className="!h-10 !bg-primary text-white rounded-md text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                                >
                                    {getWeddingSpotVenuesLoading && <Spinner className={"!border-white"} />}
                                    Get Venues
                                </Button>
                            </div>
                        </CommonCard>
                    )}

                    {/* Knot Options */}
                    {leadSourceSelected === SOURCES.LEAD_KNOT && (
                        <CommonCard>
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <CommonSelect
                                        options={providersList.map(opt => ({
                                            label: opt.name,
                                            value: opt.id?.toString()
                                        }))}
                                        value={leadProvider}
                                        onChange={(value) => setLeadProvider(value)}
                                        placeholder="Select storefront"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    disabled={getKnotStorefrontsLoading}
                                    onClick={() => getKnotStorefronts()}
                                    className="!h-10 !bg-primary text-white rounded-md text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                                >
                                    {getKnotStorefrontsLoading && <Spinner className={"!border-white"} />}
                                    Get Storefronts
                                </Button>
                            </div>
                        </CommonCard>
                    )}

                    {/* Yelp Options */}
                    {leadSourceSelected === SOURCES.LEAD_YELP && (
                        <CommonCard>
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <CommonSelect
                                        options={Array.isArray(providersList) ? providersList.map(opt => ({
                                            label: opt.name,
                                            value: opt.id?.toString()
                                        })) : []}
                                        value={leadProvider}
                                        onChange={(value) => setLeadProvider(value)}
                                        placeholder="Select location"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    disabled={getProvidersYelpLoading}
                                    onClick={() => getProvidersYelp()}
                                    className="!h-10 !bg-primary text-white rounded-md text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                                >
                                    {getProvidersYelpLoading && <Spinner className={"!border-white"} />}
                                    Get Locations
                                </Button>
                            </div>
                        </CommonCard>
                    )}

                    <div className="flex items-center gap-x-2">
                        <CommonCheckbox
                            checked={activateJob}
                            onChange={(e) =>
                                setActivateJob(e.target.checked)
                            }
                        />
                        <p className="text-sm font-medium">Activate Job</p>
                    </div>
                </div>
            ),
        },
        {
            key: "2",
            label: "Leads Destination(s)",
            children: (
                <div className="space-y-6">
                    <div>
                        <h5 className="font-semibold text-base mb-4">Leads Destinations</h5>
                        <div className="grid grid-cols-2 gap-4">
                            {destinations && Array.isArray(destinations) && destinations.map((dest) => (
                                <div key={dest.integrationCompany} className="flex items-center gap-2">
                                    <CommonCheckbox
                                        checked={destinationIds.includes(dest.integrationCompany.toString())}
                                        onChange={(e) =>
                                            handleDestinationChange(e.target.checked, dest.integrationCompany.toString())
                                        }
                                    />
                                    <p className='text-sm font-medium'>{capitalizeFirstLetter(dest.integrationCompany)}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Tripleseat Location Selection */}
                    {showTripleseatLocation && (
                        <CommonCard>
                            <h6 className="font-medium text-sm mb-3">Choose Tripleseat Location to Send Leads</h6>
                            {tripleseatLocationsLoading ? (
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <div className="size-4 rounded-full border-2 border-gray-300 !border-t-primary animate-spin" />
                                    Loading locations...
                                </div>
                            ) : (
                                <CommonSelect
                                    options={tripleseatLocations && Array.isArray(tripleseatLocations) ? tripleseatLocations.map((location) => ({
                                        label: location.name,
                                        value: location.id
                                    })) : []}
                                    value={locationIdTripleseat}
                                    onChange={(value) => setLocationIdTripleseat(value)}
                                    placeholder="Select tripleseat location"
                                />
                            )}
                        </CommonCard>
                    )}

                    {/* MailChimp Campaign Selection */}
                    {showMailChimpCampaigns && (
                        <CommonCard>
                            <h6 className="font-medium text-sm mb-3">Choose MailChimp Campaign</h6>
                            {mailChimpCampaignsLoading ? (
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <div className="size-4 rounded-full border-2 border-gray-300 !border-t-primary animate-spin" />
                                    Loading campaigns...
                                </div>
                            ) : (
                                <CommonSelect
                                    options={mailChimpCampaigns && Array.isArray(mailChimpCampaigns) ? mailChimpCampaigns.map((campaign) => ({
                                        label: campaign.name,
                                        value: campaign.id
                                    })) : []}
                                    value={campaignIdMailChimp}
                                    onChange={(value) => setCampaignIdMailChimp(value)}
                                    placeholder="Select mailchimp campaign"
                                />
                            )}
                        </CommonCard>
                    )
                    }
                </div >
            ),
        },
        {
            key: "3",
            label: "Alerts",
            children: (
                <div className="space-y-6">
                    <div>
                        <h5 className="font-semibold text-base mb-4">Alerts</h5>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Do you want to send email/sms alerts?
                                </label>
                                <CommonSelect
                                    options={[
                                        { label: "Anytime", value: "anytime" },
                                        { label: "Specific Time", value: "specifictime" },
                                    ]}
                                    value={alertSendType}
                                    onChange={(value) => setAlertSendType(value)}
                                    placeholder="Select Alert Send Time"
                                />
                            </div>

                            {alertSendType === "specifictime" && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Start Time</label>
                                        <AntTimePicker
                                            className="border hover:!border-primary focus:!border-primary w-full"
                                            format="HH:mm"
                                            value={alertSendStartTime ? dayjs(alertSendStartTime, "HH:mm") : null}
                                            onChange={(time) => setAlertSendStartTime(time ? time.format("HH:mm") : null)}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2">End Time</label>
                                        <AntTimePicker
                                            className="border hover:!border-primary focus:!border-primary w-full"
                                            format="HH:mm"
                                            value={alertSendEndTime ? dayjs(alertSendEndTime, "HH:mm") : null}
                                            onChange={(time) => setAlertSendEndTime(time ? time.format("HH:mm") : null)}
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium mb-2">Timezone</label>
                                        <CommonSelect value={timezoneSelected} onChange={(value) => setTimezoneSelected(value)} options={Array.isArray(timezoneOptions) && timezoneOptions.map((to) => {
                                            return {
                                                label: to.label,
                                                value: to.value
                                            }
                                        })} placeholder="Select Timezone" />
                                    </div>
                                </div>
                            )}

                            {alertSendType && (
                                <div>
                                    <label className="block text-sm font-medium mb-3">
                                        Choose from below users if you would like to send alerts?
                                    </label>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[420px] overflow-y-auto pr-1">
                                        {refactorAlertUsers.map((user) => (
                                            <AlertUserCard
                                                key={user.id}
                                                user={user}
                                                checked={alertUserId.includes(user.id)}
                                                onChange={handleAlertUserChange}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    const isLoading = leadSourceOptionsLoading || destinationsLoading || alertUsersLoading || timezonesLoading;

    return (
        <CommonDialog
            title={editLeadSource?.isEditable ? "Edit Import Job" : "Create Import Job"}
            description={editLeadSource?.isEditable ? "Modify the import job details and save your changes." : "Fill the form to create a new import job."}
            open={showForm}
            setOpen={setShowForm}
            onOk={handleSubmit}
            isConfirmButtonLoading={saveLeadSourceMutation.isPending}
            onCancel={() => {
                setShowForm(false);
                form.resetFields();
            }}
            okButtonTitle={editLeadSource?.isEditable ? "Update" : "Create"}
            className="relative overflow-hidden"
            width={800}
        >
            {(editLeadSource?.isEditable && leadSourceDetailLoading || isLoading) && (
                <div className="absolute -top-2 left-0 w-full h-full bg-black/15 z-10 rounded-lg flex justify-center items-center">
                    <div className="flex flex-col justify-center items-center gap-1">
                        <div className="size-6 rounded-full border-2 border-white !border-t-primary animate-spin" />
                    </div>
                </div>
            )}

            <CommonTabs
                items={tabItems}
                activeKey={activeTab}
                onChange={setActiveTab}
                className="mt-4"
            />
        </CommonDialog>
    );
};

export default EnhanceAddLeadSource;
